import { CATEGORIES } from '../constants';
import { Career, Category } from '../types';

// Simple in-memory cache to prevent redundant network requests
const careerCache: Record<string, Career> = {};
let careerIndexCache: any[] | null = null;

/**
 * Robust fetch helper that attempts multiple path strategies
 */
const fetchWithFallback = async (basePath: string, slug: string): Promise<any | null> => {
  const candidates = [
    `${basePath}${slug}.json`,
    `/${basePath}${slug}.json`,
    `./${basePath}${slug}.json`,
    // Handle cases where we might be in a subfolder or preview URL
    `${window.location.origin}${window.location.pathname.replace(/\/$/, '')}/${basePath}${slug}.json`.replace(/([^:]\/)\/+/g, "$1")
  ];

  for (const url of candidates) {
    try {
      console.debug(`Attempting to fetch career from: ${url}`);
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (e) {
      console.warn(`Fetch failed for URL: ${url}`, e);
      continue;
    }
  }
  return null;
};

const fetchIndex = async () => {
  if (careerIndexCache) return careerIndexCache;
  const start = performance.now();
  try {
    const data = await fetchWithFallback('data/careers/', 'index');
    if (data) {
      careerIndexCache = data;
      console.debug(`[Perf] Career Index Loaded: ${Math.round(performance.now() - start)}ms`);
      return careerIndexCache;
    }
    throw new Error('All index fetch attempts failed');
  } catch (error) {
    console.error('Error loading career index:', error);
    return [];
  }
};

export const getAllCareers = async (): Promise<any[]> => {
  return await fetchIndex();
};

/**
 * Fetches the shorts feed from an external URL if provided, otherwise falls back to the local index.
 */
export const getShortsFeed = async (): Promise<any[]> => {
  const externalUrl = (import.meta as any).env?.VITE_SHORTS_FEED_URL;
  
  if (externalUrl) {
    try {
      const response = await fetch(externalUrl);
      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (e) {
      console.warn('Failed to fetch external shorts feed, falling back to local index:', e);
    }
  }
  
  return await getAllCareers();
};

export const getFeaturedCareers = async (): Promise<any[]> => {
  const index = await fetchIndex();
  return index.filter((c: any) => c.featured);
};

export const getCareerBySlug = async (slug: string): Promise<Career | undefined> => {
  if (!slug) return undefined;
  
  // Normalize slug
  const cleanSlug = slug.replace(/\.json$/, '').trim().toLowerCase();
  
  if (careerCache[cleanSlug]) return careerCache[cleanSlug];
  
  try {
    const data = await fetchWithFallback('data/careers/', cleanSlug);
    
    if (data) {
      // Basic structural validation
      if (data.slug && data.slug.toLowerCase() === cleanSlug) {
        careerCache[cleanSlug] = data;
        return data;
      } else {
        console.error(`Slug mismatch in JSON for "${cleanSlug}". Found: "${data.slug}"`);
      }
    }
    
    console.error(`Career data for slug "${cleanSlug}" could not be loaded from any candidate path.`);
    return undefined;
  } catch (error) {
    console.error(`Error loading career ${cleanSlug}:`, error);
    return undefined;
  }
};

export const getCategories = (): Category[] => {
  return [...CATEGORIES].sort((a, b) => a.order - b.order);
};

export const searchCareers = (
  careers: any[],
  query: string,
  categoryFilter: string | null,
  sortMethod: 'az' | 'newest'
): any[] => {
  let result = [...careers];

  if (categoryFilter) {
    const categories = getCategories();
    const catObj = categories.find(c => c.id === categoryFilter);
    if (catObj) {
      result = result.filter(c => c.category.toLowerCase() === catObj.label.toLowerCase());
    }
  }

  if (query && query.trim()) {
    const q = query.toLowerCase().trim();
    result = result.filter(c => 
      c.title.toLowerCase().includes(q) ||
      (c.keywords || []).some((k: string) => k.toLowerCase().includes(q))
    );
  }

  result.sort((a, b) => {
    if (sortMethod === 'az') {
      return a.title.localeCompare(b.title);
    } else {
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    }
  });

  return result;
};
